import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle,
  XCircle,
  Users,
  Car,
  Phone,
  AlertCircle,
  Trash2,
  Settings,
  ArrowRight,
  X,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  类型定义                                                           */
/* ------------------------------------------------------------------ */

interface ContactPreview {
  id: string;
  name: string;
  phone: string;
  vehicle_brand?: string;
  vehicle_series?: string;
  vehicle_model?: string;
  purchase_date?: string;
  company?: string;
}

interface FailRow {
  row: number;
  reason: string;
  data: Record<string, unknown>;
}

interface UploadPreview {
  fileId: string;
  storageKey: string;
  filename: string;
  fileType: string;
  totalRows: number;
  headers: string[];
  preview: Record<string, string>[];
}

interface ImportResult {
  batchId: string;
  downloadToken: string;
  totalCount: number;
  successCount: number;
  failCount: number;
  failRows: FailRow[];
}

type UploadStatus = 'idle' | 'uploading' | 'preview' | 'importing' | 'done' | 'error';

/* ------------------------------------------------------------------ */
/*  常量                                                               */
/* ------------------------------------------------------------------ */

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

// 可选的字段映射
const DEFAULT_FIELD_OPTIONS = [
  { key: 'name', label: '姓名', required: false, removable: false },
  { key: 'phone', label: '电话', required: true, removable: false },
  { key: 'phone2', label: '备用电话', required: false, removable: false },
  { key: 'email', label: '邮箱', required: false, removable: false },
  { key: 'id_card', label: '身份证号', required: false, removable: false },
  { key: 'vehicle_brand', label: '车辆品牌', required: false, removable: false },
  { key: 'vehicle_series', label: '车系', required: false, removable: false },
  { key: 'vehicle_model', label: '车型', required: false, removable: false },
  { key: 'purchase_date', label: '购车时间', required: false, removable: false },
  { key: 'company', label: '公司', required: false, removable: false },
  { key: 'address', label: '地址', required: false, removable: false },
  { key: 'remark', label: '备注', required: false, removable: false },
];

// 自定义字段类型选项
const CUSTOM_FIELD_PRESETS = [
  '电话', '邮箱', '地址', '备注', '公司', '车型', '车系', '品牌',
  '身份证号', 'QQ', '微信', '生日', '职业', '其他',
];

/* ------------------------------------------------------------------ */
/*  组件                                                               */
/* ------------------------------------------------------------------ */

const HomePage: React.FC = () => {
  /* ---- 状态 ---- */
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPreview, setUploadPreview] = useState<UploadPreview | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [exporting, setExporting] = useState(false);
  // 上次成功上传的批次（存于本浏览器会话，刷新后可重新下载）
  const [lastBatch, setLastBatch] = useState<{ batchId: string; downloadToken: string; filename: string } | null>(null);
  
  // 列映射配置
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [showMappingModal, setShowMappingModal] = useState(false);
  // 自定义字段列表
  const [customFields, setCustomFields] = useState<Array<{ key: string; label: string; required: boolean }>>([]);
  const [newFieldLabel, setNewFieldLabel] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 加载上次成功上传的批次（刷新后仍可重新下载）
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('lastBatch');
      if (raw) setLastBatch(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  /* ---- 工具函数 ---- */
  const resetState = useCallback(() => {
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadPreview(null);
    setImportResult(null);
    setSelectedIds(new Set());
    setShowAll(false);
    setErrorMessage('');
    setDragOver(false);
    setColumnMapping({});
    setShowMappingModal(false);
  }, []);

  const validateFile = (file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return '仅支持 .xlsx、.xls、.csv 格式的文件';
    }
    if (file.size > MAX_FILE_SIZE) {
      return '文件大小不能超过 10 MB';
    }
    return null;
  };

  /* ---- 自定义字段管理 ---- */
  const addCustomField = useCallback((label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    // 生成唯一 key
    const key = `custom_${Date.now()}_${trimmed}`;
    setCustomFields((prev) => [...prev, { key, label: trimmed, required: false }]);
    setNewFieldLabel('');
  }, []);

  const removeCustomField = useCallback((key: string) => {
    setCustomFields((prev) => prev.filter((f) => f.key !== key));
    setColumnMapping((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // 合并默认字段和自定义字段
  const allFieldOptions = [
    ...DEFAULT_FIELD_OPTIONS,
    ...customFields,
  ];

  /* ---- 上传逻辑 ---- */
  const doUpload = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setUploadStatus('error');
        setErrorMessage(validationError);
        return;
      }

      resetState();
      setUploadStatus('uploading');
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 70);
          setUploadProgress(pct);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress(90);
          try {
            const result: UploadPreview = JSON.parse(xhr.responseText);
            setUploadProgress(100);
            setUploadPreview(result);
            setUploadStatus('preview');
            setShowMappingModal(true);
            
            // 尝试自动匹配列
            const autoMapping: Record<string, string> = {};
            result.headers.forEach((header) => {
              const headerLower = header.toLowerCase();
              DEFAULT_FIELD_OPTIONS.forEach((field) => {
                if (headerLower.includes(field.label.toLowerCase()) ||
                    headerLower === field.key.toLowerCase()) {
                  autoMapping[field.key] = header;
                }
              });
            });
            setColumnMapping(autoMapping);
          } catch {
            setUploadStatus('error');
            setErrorMessage('解析服务器响应失败');
          }
        } else {
          try {
            const body = JSON.parse(xhr.responseText);
            setUploadStatus('error');
            setErrorMessage(body.error || '上传失败');
          } catch {
            setUploadStatus('error');
            setErrorMessage('上传失败');
          }
        }
      });

      xhr.addEventListener('error', () => {
        setUploadStatus('error');
        setErrorMessage('网络错误，请检查网络连接后重试');
      });

      xhr.addEventListener('abort', () => {
        setUploadStatus('error');
        setErrorMessage('上传已取消');
      });

      xhr.open('POST', '/api/upload');
      xhr.send(formData);
    },
    [resetState],
  );

  /* ---- 确认导入 ---- */
  const doImport = useCallback(async () => {
    if (!uploadPreview) return;
    
    // 验证必填字段
    if (!columnMapping['phone']) {
      setErrorMessage('请选择电话号码列');
      return;
    }

    setUploadStatus('importing');
    setUploadProgress(0);
    setShowMappingModal(false);

    try {
      const response = await fetch('/api/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId: uploadPreview.fileId,
          storageKey: uploadPreview.storageKey,
          columnMapping,
        }),
      });

      const result: any = await response.json();

      if (!response.ok) {
        setUploadStatus('error');
        setErrorMessage(result.error || '导入失败');
        return;
      }

      setImportResult(result as ImportResult);
      setUploadStatus('done');
      // 记住本次批次，便于刷新后重新下载（仅存于本浏览器会话）
      try {
        sessionStorage.setItem('lastBatch', JSON.stringify({
          batchId: result.batchId,
          downloadToken: result.downloadToken,
          filename: uploadPreview?.filename || 'contacts',
        }));
        setLastBatch({
          batchId: result.batchId,
          downloadToken: result.downloadToken,
          filename: uploadPreview?.filename || 'contacts',
        });
      } catch {
        /* sessionStorage 不可用时忽略 */
      }
    } catch (err: any) {
      setUploadStatus('error');
      setErrorMessage(err.message || '导入失败');
    }
  }, [uploadPreview, columnMapping]);

  /* ---- 拖拽事件 ---- */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        doUpload(file);
      }
    },
    [doUpload],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        doUpload(file);
      }
    },
    [doUpload],
  );

  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /* ---- 表格选择 ---- */
  const toggleSelectAll = useCallback(() => {
    if (!importResult) return;
    // 这里需要从后端获取导入的联系人列表
    // 简化处理：暂时不支持选择
  }, [importResult]);

  /* ---- 导出 vCard ---- */
  const exportVCard = useCallback(
    async (contactIds?: string[], batchId?: string, downloadToken?: string) => {
      setExporting(true);
      try {
        const body: any = {};
        if (contactIds && contactIds.length > 0) {
          body.contactIds = contactIds;
        } else if (batchId) {
          // 本次导入批次，只导出该批次的联系人（避免把后台全部 vcf 一并下载）
          body.batchId = batchId;
          if (downloadToken) body.downloadToken = downloadToken;
        } else {
          throw new Error('无法确定要导出的数据范围');
        }

        const res = await fetch('/api/export', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const err: any = await res.json().catch(() => ({ error: '导出失败' }));
          throw new Error(err.error || '导出失败');
        }

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contacts_${new Date().toISOString().slice(0, 10)}.vcf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err: any) {
        alert('导出失败：' + err.message);
      } finally {
        setExporting(false);
      }
    },
    [],
  );

  /* ---- 渲染 ---- */
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">ContactConverter</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 上次批次重新下载（刷新后也可用） */}
        {lastBatch && uploadStatus === 'idle' && (
          <div className="max-w-2xl mx-auto mb-8 bg-white rounded-xl shadow-sm border border-blue-100 p-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              检测到上次上传的批次，可重新下载通讯录文件
            </span>
            <button
              onClick={() => exportVCard(undefined, lastBatch.batchId, lastBatch.downloadToken)}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Download className="w-4 h-4" />
              <span>重新下载</span>
            </button>
          </div>
        )}
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            客户数据转通讯录
          </h1>
          <p className="text-lg text-gray-600">
            上传 Excel / CSV 文件，一键转换为手机通讯录格式
          </p>
        </div>

        {/* 使用说明 */}
        <div className="max-w-3xl mx-auto mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
          <h2 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
            <span className="text-2xl">💡</span> 使用场景说明
          </h2>
          <div className="prose prose-sm max-w-none text-gray-700 space-y-3">
            <p>
              现在做自媒体（抖音/小红书/快手等）时，平台会根据算法向"可能认识的人"推荐你的视频。
              如果你想精准触达潜在客户，就需要将他们的手机号导入到手机通讯录中。
            </p>
            <p className="font-medium text-gray-800">
              使用步骤：
            </p>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>上传包含客户手机号的 Excel/CSV 文件，系统会自动转换为通讯录格式</li>
              <li>下载生成的 <code className="bg-white px-2 py-0.5 rounded text-sm">.vcf</code> 通讯录文件</li>
              <li>通过微信传输到手机</li>
              <li>打开文件时选择 <strong>"用电话本打开"</strong> 即可批量导入</li>
            </ol>
          </div>
        </div>

        {/* 上传区域 */}
        {uploadStatus === 'idle' && (
          <div
            className={`max-w-2xl mx-auto border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer
              ${dragOver 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClickUpload}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInputChange}
            />
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-lg text-gray-700 mb-2">
              拖拽文件到此处，或
              <span className="text-blue-600 font-medium"> 点击选择文件</span>
            </p>
            <p className="text-sm text-gray-500">
              支持格式：.xlsx、.xls、.csv | 最大 10 MB
            </p>
          </div>
        )}

        {/* 上传中 */}
        {(uploadStatus === 'uploading' || uploadStatus === 'importing') && (
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm p-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
            <p className="text-center text-gray-700 mb-4">
              {uploadStatus === 'uploading' ? '正在上传文件...' : '正在导入数据...'}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">
              {uploadProgress}%
            </p>
          </div>
        )}

        {/* 错误提示 */}
        {uploadStatus === 'error' && (
          <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-2xl p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-800 mb-1">
                  处理失败
                </h3>
                <p className="text-red-700">{errorMessage}</p>
              </div>
            </div>
            <button
              onClick={resetState}
              className="mt-4 w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              重新上传
            </button>
          </div>
        )}

        {/* 列映射配置弹窗 */}
        {showMappingModal && uploadPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* 弹窗头部 */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">配置列映射</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    请选择 Excel 表格中各列对应的字段（电话为必填）
                  </p>
                </div>
                <button
                  onClick={() => setShowMappingModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* 弹窗内容 */}
              <div className="flex-1 overflow-auto p-6">
                {/* 数据预览 */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">数据预览（前3行）</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          {uploadPreview.headers.map((header) => (
                            <th key={header} className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {uploadPreview.preview.map((row, idx) => (
                          <tr key={idx} className="border-b last:border-b-0">
                            {uploadPreview.headers.map((header) => (
                              <td key={header} className="px-4 py-2 text-gray-600 truncate max-w-[150px]">
                                {row[header] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 列映射选择 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allFieldOptions.map((field) => (
                    <div key={field.key} className="flex items-center space-x-3">
                      <label className="w-24 text-sm font-medium text-gray-700 flex items-center">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <select
                        value={columnMapping[field.key] || ''}
                        onChange={(e) => {
                          setColumnMapping((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- 不导入 --</option>
                        {uploadPreview.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                      {'removable' in field && field.removable !== false && (
                        <button
                          onClick={() => removeCustomField(field.key)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="删除此字段"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* 添加自定义字段 */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">添加自定义字段</h3>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={newFieldLabel}
                      onChange={(e) => setNewFieldLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomField(newFieldLabel);
                        }
                      }}
                      placeholder="输入字段名称，如：电话2、邮箱1、地址2..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={() => addCustomField(newFieldLabel)}
                      disabled={!newFieldLabel.trim()}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 transition-colors text-sm font-medium"
                    >
                      + 添加
                    </button>
                  </div>
                  {/* 快捷标签 */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {CUSTOM_FIELD_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        onClick={() => addCustomField(preset)}
                        className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-colors"
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>

                {errorMessage && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-sm text-red-700">{errorMessage}</span>
                  </div>
                )}
              </div>

              {/* 弹窗底部 */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  共 {uploadPreview.totalRows} 行数据
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowMappingModal(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={doImport}
                    disabled={!columnMapping['phone']}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    <span>确认导入</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 导入结果 */}
        {uploadStatus === 'done' && importResult && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* 统计卡片 */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-6 text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {importResult.totalCount}
                </div>
                <div className="text-sm text-gray-500">总行数</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 text-center">
                <div className="text-3xl font-bold text-green-600 mb-1">
                  {importResult.successCount}
                </div>
                <div className="text-sm text-gray-500">成功导入</div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 text-center">
                <div className="text-3xl font-bold text-red-600 mb-1">
                  {importResult.failCount}
                </div>
                <div className="text-sm text-gray-500">失败</div>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => exportVCard(undefined, importResult.batchId, importResult.downloadToken)}
                disabled={exporting || importResult.successCount === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>{exporting ? '导出中...' : '下载通讯录文件 (.vcf)'}</span>
              </button>
              <button
                onClick={resetState}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>清空数据</span>
              </button>
            </div>

            {/* 失败行列表 */}
            {importResult.failRows.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    导入失败的行
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          行号
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          失败原因
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          原始数据
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {importResult.failRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {row.row}
                          </td>
                          <td className="px-6 py-4 text-sm text-red-600">
                            {row.reason}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <pre className="text-xs overflow-x-auto max-w-md">
                              {JSON.stringify(row.data, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-500">
            © 2024 ContactConverter. 客户通讯录转换工具
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
